import type { ExportRequestResponse, ExportSummary } from '@gozoki/api-types'
import { type ExternalToast, toast } from 'sonner'
import {
  EXPORT_FAILED_TITLE,
  type ExportToastKind,
  exportRequestedToast,
  exportSettledToast,
} from '~/lib/export-jobs'
import { tuyauClient } from '~/lib/tuyau'
import { getApiErrorMessage } from '~/lib/utils'

/**
 * Toasts et téléchargement des exports en tâche de fond (lot S8.4, conception S8 §2.9 sans le
 * mail, arbitrage 24), sur le `<Toaster>` sonner de `routes/__root.tsx`. Deux toasts par export :
 * la demande (« Export lancé ») puis l'issue (prêt, échec, annulé), qui ferme le premier. Pas un
 * seul toast mis à jour : sonner y garde le minuteur du premier, et une issue arrivée au bout de
 * 3 s disparaîtrait une seconde plus tard.
 */

const requestToastId = (exportId: string) => `export:${exportId}:demande`
const outcomeToastId = (exportId: string) => `export:${exportId}:issue`

/** Le temps de cliquer sur « Télécharger ». */
const ACTION_TOAST_DURATION_MS = 10_000

const showOutcome = (
  exportId: string,
  kind: ExportToastKind,
  title: string,
  options: ExternalToast,
) => {
  toast.dismiss(requestToastId(exportId))
  toast[kind](title, { ...options, id: outcomeToastId(exportId) })
}

const showFailure = (exportId: string, label: string, message: string) =>
  showOutcome(exportId, 'error', EXPORT_FAILED_TITLE, { description: `${label} · ${message}` })

/**
 * Ouvre l'URL présignée (60 s) : S3 la sert en `Content-Disposition: attachment`, le navigateur
 * télécharge sans quitter la page.
 */
const openDownloadUrl = (url: string) => {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.rel = 'noopener'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

/**
 * `GET /exports/:id/download`, qui revérifie les droits du moment, puis ouverture de l'URL. Échec
 * (fichier expiré, droit retiré) : toast avec le message du contrat, et `false`.
 */
const downloadExportFile = async (exportId: string, label: string): Promise<boolean> => {
  try {
    const { url } = await tuyauClient.request('exports.download', {
      params: { publicId: exportId },
    })
    openDownloadUrl(url)
    return true
  } catch (error) {
    showFailure(
      exportId,
      label,
      getApiErrorMessage(error, 'Le fichier n’a pas pu être téléchargé.'),
    )
    return false
  }
}

/** Toast d'un fichier téléchargé, avec « Télécharger » pour le reprendre (nouvelle URL). */
const showDownloaded = (exportId: string, label: string, title: string, description: string) =>
  showOutcome(exportId, 'success', title, {
    description,
    duration: ACTION_TOAST_DURATION_MS,
    action: { label: 'Télécharger', onClick: () => void downloadExportFile(exportId, label) },
  })

/** Réponse `202` : « Export lancé », ou fichier identique déjà prêt, téléchargé aussitôt. */
export const announceRequestedExport = (response: ExportRequestResponse, label: string) => {
  const { id, status } = response.export
  const { title, description } = exportRequestedToast(response, label)
  if (status === 'ready') {
    showDownloaded(id, label, title, description)
    void downloadExportFile(id, label)
    return
  }
  toast.info(title, { id: requestToastId(id), description })
}

/** Statut final d'un export suivi : téléchargement et « Prêt », ou échec, ou annulation. */
export const announceSettledExport = async (
  summary: Pick<ExportSummary, 'id' | 'status' | 'error' | 'fileName'>,
  label: string,
): Promise<void> => {
  const { kind, title, description } = exportSettledToast(summary, label)
  if (summary.status !== 'ready') {
    showOutcome(summary.id, kind, title, { description })
    return
  }
  if (await downloadExportFile(summary.id, label)) {
    showDownloaded(summary.id, label, title, description)
  }
}

/** Suivi refusé (export introuvable, droit retiré) : échec avec le message du contrat. */
export const announceLostExport = (exportId: string, label: string, error: unknown) => {
  showFailure(exportId, label, getApiErrorMessage(error, 'Le suivi de l’export a échoué.'))
}

/** Route d'export en échec avant toute demande (`422`, `403`, `429`) : le message du corps. */
export const announceRefusedExport = (label: string, error: unknown) => {
  toast.error(EXPORT_FAILED_TITLE, {
    description: `${label} · ${getApiErrorMessage(error, 'L’export n’a pas pu être lancé.')}`,
  })
}
